import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  signal,
  computed,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IconComponent } from '../../icons/icon.component';
import { AvatarConfig } from './avatar.model';

export enum AvatarSize {
  ExtraSmall = 1,
  Small = 2,
  Medium = 3,
  Large = 4,
  ExtraLarge = 5,
}

/**
 * Displays a circular avatar that:
 *  - uses `imageUrl` or `fallbackSrc` as its <img> source
 *  - on load-error, retries once then falls back to an icon
 *  - can optionally be wrapped in a routerLink
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-avatar',
  standalone: true,
  imports: [CommonModule, RouterModule, NgClass, IconComponent],
  templateUrl: './avatar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  /** alt text for the image or icon */
  public altText = input<string>('');

  /** primary image URL */
  public imageUrl = input<string>('');

  /** routerLink target, if you want the avatar clickable */
  public routerLink = input<string | string[]>('');

  /** size enum (ExtraSmall → ExtraLarge) */
  public size = input<AvatarSize>(AvatarSize.Small);

  /** optional badge icon name */
  public badgeIcon = input<string>('');

  /** alt text for the badge icon */
  public badgeAltText = input<string>('');

  /** advanced configuration  */
  public config = input<AvatarConfig>(null);

  public readonly avatarSizeClassMap = {
    [AvatarSize.ExtraSmall]: 'tw-h-4 tw-w-4',
    [AvatarSize.Small]: 'tw-h-6 tw-w-6',
    [AvatarSize.Medium]: 'tw-h-8 tw-w-8',
    [AvatarSize.Large]: 'tw-h-12 tw-w-12',
    [AvatarSize.ExtraLarge]: 'tw-h-16 tw-w-16',
  };

  public avatarSize = AvatarSize;

  /** private signal holding the current <img> src or `null` */
  public src = signal<string | null>(null);

  /** When imageUrl and (optional) fallbackSrc have failed use a fallback icon  */
  public readonly useFallbackIcon = computed(() => this.src() === '');

  constructor() {
    // whenever imageUrl or fallbackSrc changes, reset imgSrc
    effect(
      () => {
        this.src.set(this.imageUrl() || (this.config()?.fallbackSrc ?? ''));
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Called from <img (error)>:
   * 1) if we haven’t tried the fallbackSrc yet, switch to it
   * 2) otherwise imgSrc being null will show the fallbackIcon
   */
  public imageDidNotLoad(): void {
    const fallbackSrc = this.config()?.fallbackSrc;
    if (fallbackSrc && this.src() !== fallbackSrc) {
      this.src.set(fallbackSrc);
    } else {
      this.src.set('');
    }
  }
}
